import React from 'react';

import { Box, Display, Flex, Heading, Image, Paragraph } from '@rover/kibble/core';
import type { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import Section from '@rover/kibble/patterns/Section';
import type { ThemeSpacing } from '@rover/kibble/styles/themeTypes';
import type { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import UnorderedList, {
  Icons,
  MarkerImage,
} from '@rover/react-lib/src/components/lists/UnorderedList/UnorderedList';

import defaultServiceBenefitsImage from './images/three_dogs_and_cloud.png';
import defaultServiceBenefitsMobileImage from './images/two_dogs.png';

export type ServiceBenefitsImageSrcSet = {
  desktop: {
    src: string;
    width: number;
    height: number;
  };
  mobile: {
    src: string;
    width: number;
    height: number;
  };
};

type ServiceBenefitsProps = {
  serviceBenefitsImageSrcSet?: ServiceBenefitsImageSrcSet;
  title?: React.ReactNode;
  headingVariant?: HeadingVariant;
  /** Props passed to both desktop and mobile Image components (e.g. sx for borderRadius) */
  imageProps?: Partial<React.ComponentProps<typeof Image>>;
  /** Gap between image and text columns on desktop */
  columnGap?: string;
  /** Gap between the mobile image and the section below it (mobile only; desktop stacks in columns) */
  mobileImageGap?: ThemeSpacing;
  /** Width of the image column on desktop (e.g. '585px' or '50%') */
  imageWidth?: string;
  /** Stretch the desktop image to the text column's height. When false, the image keeps its
   * aspect ratio and is centered beside the text, so its size doesn't depend on the item count. */
  stretchImage?: boolean;
  /** Outer margin-y for the whole section — the caller's to set per the landing four-layer
   * model's spacing invariant, not this generalized component's. A responsive [mobile, desktop]
   * array, same shape as Section's own my/mt/mb. Defaults to the historical
   * ServiceLandingSection margin-y tokens for existing callers that don't pass it. */
  marginY?: SpacingSystemType;
  /** Background color for the section, responsive array supported */
  background?: React.ComponentProps<typeof Section>['background'];
  /** Attaches a ref (e.g. from useSectionRef) to the section for view tracking */
  htmlRef?: React.ComponentProps<typeof Section>['htmlRef'];
  children?: React.ReactNode;
};

type ChildrenProps = {
  children?: React.ReactNode;
};

type ListItemProps = React.ComponentProps<typeof Box> & {
  title: React.ReactNode;
  children: React.ReactNode;
  /** Overrides the list's default checkmark with this image for this item only. */
  markerImage?: MarkerImage;
};

const MAX_TEXT_WIDTH = '558px';
// Default marginY only — matches ServiceLanding/components/ServiceLandingSection's margin-y
// tokens. Kept as local literals (rather than importing from that ServiceLanding-specific file)
// so this generalized component carries no page-family knowledge - see landing/CLAUDE.md's
// four-layer model. Callers that care about their own outer spacing pass marginY instead.
const DEFAULT_MARGIN_Y: [ThemeSpacing, ThemeSpacing] = ['12x', '24x'];

// marginY may be a single token or a responsive array (same shape Section's own my/mt/mb
// accept) — normalize to [mobile, desktop] so both breakpoints are addressable below.
const marginYByBreakpoint = (marginY: SpacingSystemType): [ThemeSpacing, ThemeSpacing] =>
  Array.isArray(marginY) ? [marginY[0], marginY[marginY.length > 1 ? 1 : 0]] : [marginY, marginY];

const SERVICE_BENEFITS_IMAGES: ServiceBenefitsImageSrcSet = {
  desktop: {
    src: defaultServiceBenefitsImage,
    width: 589,
    height: 493,
  },
  mobile: {
    src: defaultServiceBenefitsMobileImage,
    width: 242,
    height: 189,
  },
};

const ServiceBenefits = ({
  serviceBenefitsImageSrcSet = SERVICE_BENEFITS_IMAGES,
  title,
  headingVariant = 'display',
  imageProps,
  columnGap = '6x',
  mobileImageGap = '0x',
  imageWidth = '50%',
  stretchImage = true,
  marginY = DEFAULT_MARGIN_Y,
  background = ['secondary', 'primary'],
  htmlRef,
  children,
}: ServiceBenefitsProps): JSX.Element => {
  const { desktop, mobile } = serviceBenefitsImageSrcSet;
  const [mobileMarginY, desktopMarginY] = marginYByBreakpoint(marginY);
  return (
    <>
      {/* Mobile: Image appears on top of the section */}
      {/* and service landing section top margins are applied */}
      <Flex
        display={['flex', 'none']}
        justifyContent="center"
        width="100%"
        mt={mobileMarginY}
        mb={[mobileImageGap, '0x']}
      >
        <Box
          width={`${mobile.width}px`}
          maxWidth="80%"
          sx={{ aspectRatio: mobile.width / mobile.height }}
          data-testid="service-benefits-image-mobile"
        >
          <Image src={mobile.src} description="" width="100%" height="100%" {...imageProps} />
        </Box>
      </Flex>

      <Section
        htmlRef={htmlRef}
        mt={['0x', desktopMarginY]} // on mobile, margins are applied to the image container above
        mb={marginY}
        data-testid="service-benefits-container"
        background={background}
        position="relative"
      >
        <Flex
          data-testid="service-benefits-two-column-layout"
          flexDirection={['column', 'row']}
          alignItems="center"
          justifyContent="center"
          sx={{
            gap: ['0', columnGap],
          }}
          width="100%"
        >
          <Box
            display={['none', 'none', 'block']}
            maxWidth={`${desktop.width}px`}
            flexBasis={['0%', '40%', imageWidth]}
            alignSelf={stretchImage ? 'stretch' : 'center'}
            sx={stretchImage ? undefined : { aspectRatio: desktop.width / desktop.height }}
            data-testid="service-benefits-image-desktop"
          >
            <Image
              src={desktop.src}
              width="100%"
              height="100%"
              description=""
              resizeMethod="contain"
              {...imageProps}
            />
          </Box>

          {/* The flex shorthand overrides flexBasis. From 768px the column must be able to shrink,
              or a long heading widens it past the section and the row overflows both edges. */}
          <Box
            px={['5x', '0x']}
            pt={['6x', '0x']}
            flexBasis={['100%', '60%']}
            flex={['none', '0 1 auto', '1']}
            data-testid="service-benefits-text-column"
          >
            {title &&
              (headingVariant === 'display' ? (
                <Display webProps={{ as: 'h3', responsive: true }} size="400" textAlign="left">
                  {title}
                </Display>
              ) : (
                <Heading webProps={{ as: 'h3', responsive: true }} size="400" textAlign="left">
                  {title}
                </Heading>
              ))}
            {children}
          </Box>
        </Flex>
      </Section>
    </>
  );
};

ServiceBenefits.List = (props: ChildrenProps): JSX.Element => (
  <Flex width="100%" justifyContent="flex-start">
    <UnorderedList icon={Icons.CheckMark} width="100%" maxWidth={MAX_TEXT_WIDTH}>
      {props.children}
    </UnorderedList>
  </Flex>
);

// `markerImage` isn't rendered by this component directly — UnorderedList reads it straight off
// this element's props to pick this item's marker. It's declared on ListItemProps so callers get
// it typed here; Box's shouldForwardProp already keeps it (like any non-DOM prop) out of the DOM
// when it flows through with the rest of boxProps below.
ServiceBenefits.ListItem = ({ title, children, ...boxProps }: ListItemProps): JSX.Element => (
  <Box marginBottom="8x" {...boxProps}>
    <Heading mb="1x">{title}</Heading>
    <Paragraph textColor="tertiary">{children}</Paragraph>
  </Box>
);

export default ServiceBenefits;
